import React, { useState, useEffect } from "react";
import { CheckSquare, X, UserCheck, Users, Target } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";

import { apiUrl } from "../../constants";
const ActivityAssignment = ({ onSubmit }) => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");
  const [activities, setActivities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const activitiesResponse = await axios.get(
          `${apiUrl}/activity/activityfetch`,
        );
        setActivities(activitiesResponse.data || []);

        const employeesResponse = await axios.get(
          `${apiUrl}/insights/employeefetch`,
        );
        setEmployees(employeesResponse.data || []);
      } catch (error) {
        setError("Error fetching data. Please try again.");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");

    try {
      if (!selectedEmployee || !selectedActivity) {
        setError("Please fill in all required fields");
        setSubmitting(false);
        return;
      }

      const assignmentData = {
        employeeId: selectedEmployee,
        activityId: selectedActivity,
      };
      const resperassign = await axios.post(
        `${apiUrl}/insights/activityassignpeople`,
        {
          activityName: selectedActivity,
          assignedPersonName: selectedEmployeeName,
          count: 0,
        },
      );

      if (resperassign.data.success) {
        await axios.put(`${apiUrl}/insights/employeeactivityupdate`, {
          empid: selectedEmployee,
          activityName: selectedActivity,
        });

        setSuccess(true);
        toast.success("Activity Assigned", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        if (onSubmit) {
          onSubmit(assignmentData);
        }

        setTimeout(() => {
          setSelectedEmployee("");
          setSelectedEmployeeName("");
          setSelectedActivity("");
          setSuccess(false);
        }, 2000);
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Error assigning activity. Please try again.",
      );
      toast.error("Not Assigned", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      console.error("Error assigning activity:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 mb-6 text-white shadow-lg">
          <h1 className="text-3xl font-bold text-center mb-2">
            Activity Assignment
          </h1>
          <p className="text-center opacity-90">
            Assign activities to employees
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <div className="text-gray-600 text-lg">Loading data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 mb-6 text-white shadow-lg">
        <h1 className="text-3xl font-bold text-center mb-2">
          Activity Assignment
        </h1>
        <p className="text-center opacity-90">Assign activities to employees</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl shadow-sm">
          <div className="flex items-center">
            <CheckSquare className="w-5 h-5 mr-2" />
            Activity assigned successfully!
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl shadow-sm">
          <div className="flex items-center">
            <X className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Assignment Form */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
        <div className="flex items-center mb-6">
          <UserCheck className="w-6 h-6 text-blue-600 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">
            Activity Assignment
          </h2>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Select Employee *
                </div>
              </label>
              <div className="relative">
                <select
                  value={selectedEmployee}
                  onChange={(e) => {
                    setSelectedEmployee(e.target.value);
                    const selectedEmp = employees.find(
                      (emp) => emp.empid === e.target.value,
                    );
                    setSelectedEmployeeName(
                      selectedEmp ? selectedEmp.name : "",
                    );
                  }}
                  required
                  className="w-full h-full min-w-[300px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                >
                  <option value="">Choose an employee...</option>
                  {employees.map((employee) => (
                    <option key={employee.empid} value={employee.empid}>
                      {employee.name} (ID: {employee.empid})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  Select Activity *
                </div>
              </label>
              <div className="relative">
                <select
                  value={selectedActivity}
                  onChange={(e) => setSelectedActivity(e.target.value)}
                  required
                  className="w-full h-full min-w-[300px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                >
                  <option value="">Choose an activity...</option>
                  {activities.map((activity) => (
                    <option key={activity.name} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Assignment Preview */}
          {selectedEmployee && selectedActivity && (
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-center">
                Assignment Preview
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p>
                    <strong>Employee:</strong>{" "}
                    {selectedEmployeeName || "Not specified"}
                  </p>
                  <p>
                    <strong>Employee ID:</strong>{" "}
                    {selectedEmployee || "Not specified"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p>
                    <strong>Activity:</strong>{" "}
                    {selectedActivity || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="mt-4 text-center p-4 bg-white bg-opacity-20 rounded-xl">
                <div className="text-lg font-bold">
                  {selectedEmployeeName} ← {selectedActivity}
                </div>
                <div className="text-sm opacity-90">Activity Assignment</div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-2xl hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Assigning Activity...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <UserCheck className="w-5 h-5 mr-2" />
                Assign Activity
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityAssignment;
